import Maintenance from '@/components/Maintenance';
import RateLimited from '@/components/RateLimited/RateLimited';
import StudyNav from '@/components/Navigation/StudyNav';
import TeamNav from '@/components/Navigation/TeamNav';
import ExportDownloadConfirmation from '@/components/Studies/StudyExports/ExportDownloadConfirmation/ExportDownloadConfirmation';
import { UnSupportedClientVersion } from '@/components/UnSupportedClientVersion/UnSupportedClientVersion.tsx';
import { isCancelled, isNotApproved } from '@/helpers';
import Account from '@/pages/account';
import Dashboard from '@/pages/dashboard';
import ForgotPassword from '@/pages/forgot-password';
import { Login } from '@/pages/login/Login.tsx';
import PasswordReset from '@/pages/reset-password';
import ShowStudy from '@/pages/studies/show';
import type { ComponentType } from 'react';
import {
  onlySupportUsers,
  onlyTeamOwners,
  resetReduxSettings,
  withGlossary,
  withSpacing,
  withStudy,
  withStudyV2,
} from './routeHOCs';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LazyRouteComponent = ComponentType<any>;

interface LazyRouteModule {
  component: LazyRouteComponent;
}

export type LazyRoute = () => Promise<LazyRouteModule>;

const lazyRoute = (loadComponent: () => Promise<LazyRouteComponent>): LazyRoute => {
  let componentPromise: Promise<LazyRouteModule> | undefined;

  return () => {
    if (componentPromise === undefined) {
      componentPromise = loadComponent()
        .then((component) => ({ component }))
        .catch((error: unknown) => {
          componentPromise = undefined;
          throw error;
        });
    }

    return componentPromise;
  };
};

export const routePageTitles: Readonly<Record<string, string>> = {
  '/login': 'Login',
  '/forgot-password': 'Forgot Password',
  '/reset-password': 'Reset Password',
  '/create-account': 'Create Account',
  '/maintenance': 'Maintenance',
  '/rate-limited': 'Rate Limited',
  '/unsupported-client-version': 'Unsupported Version',
  '/debug': 'Debug',
  '/': 'Studies',
  '/dashboard': 'Studies',
  '/samples': 'Samples',
  '/tasks': 'Tasks',
  '/account': 'Account',
  '/colony': 'Colony',
  '/colony/add': 'Add to Colony',
  '/colony/add/study/:studyId': 'Add to study',
  '/colony/:id/add-animals': 'Add Animals',
  '/colony/edit/animals': 'Edit Animals',
  '/colony/edit/cages': 'Edit Cages',
  '/colony/cages/manage': 'Manage Cages',
  '/approvals': 'Reviews',
  '/approvals/:approvalRequestId/:tab?': 'Review',
  '/requests': 'Requests',
  '/requests/:requestId/:tab?': 'Request',
  '/ai-agents': 'AI Agents',
  '/agents/:slug/setup': 'Agent Setup',
  '/ai-agents/:slug/sessions/:sessionApiId': 'Agent Session',
  '/studies/new': 'New Study',
  '/team': 'Manage Team',
  '/team/glossary/metadata': 'Metadata',
  '/team/glossary/observations': 'Observations Glossary',
  '/team/glossary/samples': 'Samples Glossary',
  '/team/glossary/deceased': 'Deceased Reasons',
  '/team/glossary/species-and-strains': 'Species & Strains',
  '/team/glossary/treatments': 'Treatments',
  '/team/glossary/presets': 'Presets',
  '/team/glossary/presets/:presetAction': 'Preset Builder',
  '/team/glossary/presets/:presetApiId/:presetAction': 'Preset Builder',
  '/team/studies/approvals/*': 'Review templates',
  '/team/studies/requests/*': 'Request Templates',
  '/team/studies/cage-cards': 'Cage Cards',
  '/team/studies/devices': 'Devices',
  '/team/studies/groups': 'Treatment Groups',
  '/team/studies/study-codes/*': 'Study Codes',
  '/team/studies/forms': 'Forms',
  '/team/studies/forms/:formId': 'Edit Form',
  '/team/integrations/sso': 'Single Sign-On',
  '/team/integrations/api': 'API Keys',
  '/team/integrations/webhooks': 'Webhooks',
  '/team/integrations/webhooks/:webhookApiId/events': 'Webhook Events',
  '/team/benchling-sync': 'Benchling Sync',
  '/team/benchling-sync/:benchlingBackfillApiId/events': 'Benchling Sync Events',
  '/team/admin-console/team-config': 'Team Config',
  '/team/admin-console/feature-flags': 'Feature Flags',
  '/team/admin-console/sync': 'Benchling Sync',
  '/team/admin-console/tickatlab': 'Tick@lab Config',
  '/exports/:exportApiId/download': 'Download Export',
  '/studies/:studyApiId/exports/:exportApiId/download': 'Download Export',
};

export const studyRoutePageTitles: Readonly<Record<string, string>> = {
  '/studies/:id': '',
  '/studies/:id/schedule': 'Schedule',
  '/studies/:id/animals': 'Animals',
  '/studies/:id/animals/edit': 'Edit Animals',
  '/studies/:id/animals/:subjectId': 'Animal',
  '/studies/:id/observations': 'Observations',
  '/studies/:id/settings': 'Study Settings',
  '/studies/:id/randomization': 'Randomization',
  '/studies/:id/randomization/:reportApiId': 'Randomization',
  '/studies/:id/recaging': 'Recaging',
  '/studies/:id/import': 'Import Measurements',
  '/studies/:id/datatables': 'Data tables',
  '/studies/:id/datatables/create': 'New Data Table',
  '/studies/:id/datatables/:dataTableId': 'Data Table',
  '/studies/:id/templates/datatables/create': 'New Data Table from Template',
  '/studies/:id/move-animals': 'Move Animals',
  '/studies/:id/dosing': 'Dosing',
  '/studies/:id/dosing/create/:classification': 'New Dosing',
  '/studies/:id/attachments': 'Attachments',
  '/studies/:id/samples': 'Samples',
  '/studies/:id/samples/edit': 'Edit Samples',
  '/studies/:id/samples/execute': 'Collect Samples',
  '/studies/:id/samples/execute/collect': 'Collect Samples',
  '/studies/:id/samples/execute/dosing': 'Collect Samples',
  '/studies/:id/samples/reserved/execute': 'Collect Samples',
  '/studies/:id/samples/reserved/execute/collect': 'Collect Samples',
  '/studies/:id/samples/reserved/execute/dosing': 'Collect Samples',
  '/studies/:id/export': 'Exports',
  '/studies/:id/audit-log': 'Audit Log',
  '/studies/:id/benchling-sync': 'Benchling Sync',
  '/studies/:id/workflow': 'Workflow',
  '/studies/:id/cages': 'Cages',
  '/studies/:id/study-groups/:tab?': 'Treatment groups',
  '/studies/:id/graphs': 'Graphs',
  '/studies/:id/approvals/:approvalRequestId/:tab?': 'Review',
  '/studies/:id/requests': 'Create Request',
  '/studies/:id/requests/:requestId/:tab?': 'Edit Request',
};

export const notLoggedInRoutes = [
  {
    path: '/maintenance',
    component: Maintenance,
    name: 'Maintenance',
    exact: true,
  },
  {
    path: '/rate-limited',
    component: RateLimited,
    name: 'RateLimited',
    exact: true,
  },
  {
    path: '/login',
    component: Login,
    private: false,
    exact: true,
  },
  {
    path: '/forgot-password',
    component: ForgotPassword,
    private: false,
    exact: true,
  },
  {
    path: '/reset-password',
    component: PasswordReset,
    private: false,
    exact: true,
  },
  {
    path: '/debug',
    lazy: lazyRoute(() => import('@/components/AppDebug/AppDebug.tsx').then((module) => module.AppDebug)),
    private: false,
    exact: true,
  },
  {
    path: '/unsupported-client-version',
    component: UnSupportedClientVersion,
    private: false,
    exact: true,
  },
];

export const loggedInRoutes = [
  {
    path: '/',
    component: resetReduxSettings(Dashboard),
    private: true,
    exact: true,
  },
  {
    path: '/dashboard',
    component: resetReduxSettings(Dashboard),
    private: true,
    exact: true,
  },
  {
    path: '/samples',
    lazy: lazyRoute(() => import('@/components/Samples/TeamSamplesTable').then((module) => module.TeamSamplesTable)),
    private: true,
    exact: true,
  },

  {
    path: '/tasks',
    private: true,
    exact: true,
    canCollapseNav: true,
    lazy: lazyRoute(() => import('@/pages/tasks').then((module) => resetReduxSettings(module.default))),
  },
  {
    path: '/colony',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/colony/colony').then((module) => module.default)),
    name: 'Colony',
  },
  {
    path: '/colony/add',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/colony/creation').then((module) => module.default)),
    name: 'ColonyCreation',
  },
  {
    path: '/colony/edit/cages',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/colony/bulkCages').then((module) => module.default)),
    name: 'ColonyBulkCages',
  },
  {
    path: '/colony/edit/animals',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/colony/bulkAnimals').then((module) => module.default)),
    name: 'ColonyBulkAnimals',
  },
  {
    path: '/colony/:id/add-animals',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/colony/addAnimals').then((module) => module.default)),
    name: 'ColonyEdit',
  },
  {
    path: '/colony/add/study/:studyId',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/colony/addToStudy').then((module) => module.default)),
    name: 'ColonyAddToStudy',
  },
  {
    path: '/colony/cages/manage',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Colony/Cages/Manage').then((module) => module.default)),
    name: 'ColonyManage',
  },
  {
    path: '/approvals',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Approvals').then((module) => module.default)),
    name: 'Approvals',
  },
  {
    path: '/approvals/:approvalRequestId/:tab?',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Approvals/ApprovalDetails').then((module) => module.default)),
    name: 'ApprovalDetails',
  },
  {
    path: '/ai-agents',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Agents/AgentsPage/AgentsPage').then((module) => module.default)),
    name: 'Agents',
  },
  {
    path: '/agents/:slug/setup',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Agents/AgentSetupPage/AgentSetupPage').then((module) => module.default)),
    name: 'AgentSetup',
  },
  {
    path: '/ai-agents/:slug/sessions/:sessionApiId',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Agents/AgentSessionPage/AgentSessionPage').then((module) => module.default)
    ),
    name: 'AgentSession',
    skipApiIdMapping: true,
  },
  {
    path: '/requests',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Requests').then((module) => module.default)),
    name: 'Requests',
  },
  {
    path: '/requests/:requestId/:tab?',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Requests/RequestDetails').then((module) => module.default)),
    name: 'RequestDetails',
  },
  {
    path: '/studies/new',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/studies/new').then((module) => resetReduxSettings(module.default))),
  },
  {
    path: '/studies/:id',
    private: true,
    exact: true,
    component: ShowStudy,
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/schedule',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Studies/Show/StudySchedule').then((module) => module.StudySchedule)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/recaging',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/studies/recaging').then((module) => module.default)),
    name: 'Studies/Recaging',
  },
  {
    path: '/studies/:id/animals',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/animals').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/animals/edit',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Colony/Edit/BulkAnimals').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/observations',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/observations/observations').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/settings',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/studies/settings').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/randomization',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/pages/studies/randomization').then((module) =>
        withStudyV2(module.default, {
          includes: 'project',
          restrictedAccessFns: [isNotApproved, isCancelled],
        })
      )
    ),
    navigation: StudyNav,
    canCollapseNav: true,
  },

  {
    path: '/studies/:id/randomization/:reportApiId',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Studies/Randomization/Report').then((module) => module.default)),
    navigation: StudyNav,
  },
  {
    path: '/studies/:id/import',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/import/ImportMeasurements').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/datatables',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Studies/DataTables/DataTables.tsx').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/datatables/create',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/dataTables/Create').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/templates/datatables/create',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Studies/Templates/CreateDataTable/CreateDataTableFromTemplate.tsx').then(
        (module) => module.default
      )
    ),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/move-animals',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Modals/MoveToStudy/PresetMapping/PresetMapping.tsx').then((module) => module.PresetMapping)
    ),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/dosing',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Studies/Dosing/List/ListDosing').then((module) => module.ListDosing)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/dosing/create/:classification',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Studies/Dosing/Create/CreateDosing').then((module) => module.CreateDosing)
    ),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/attachments',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/attachments/attachments').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/samples',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Samples/SamplesView').then((module) => module.SamplesView)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/samples/edit',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Samples/EditSamples/BulkSampleEdit').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/samples/execute',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Samples/SamplePlanTable/Execute/ExecuteSamplesSelectAnimalsStep.tsx').then(
        (module) => module.default
      )
    ),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/samples/execute/collect',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Samples/SamplePlanTable/Execute/ExecuteSamplesCollectStepWrapper.tsx').then(
        (module) => module.default
      )
    ),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/samples/execute/dosing',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Samples/SamplePlanTable/Execute/ExecuteSamplesSetDosingStep.tsx').then(
        (module) => module.default
      )
    ),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/samples/reserved/execute',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Samples/SampleNamesTable/Execute/ExecuteReservedSamplesSelectStep.tsx').then(
        (module) => module.default
      )
    ),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/samples/reserved/execute/collect',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Samples/SampleNamesTable/Execute/ExecuteReservedSamplesCollectStep.tsx').then(
        (module) => module.default
      )
    ),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/samples/reserved/execute/dosing',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Samples/SampleNamesTable/Execute/ExecuteReservedSamplesSetDosingStep.tsx').then(
        (module) => module.default
      )
    ),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/export',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Studies/StudyExportsContainer/StudyExportsContainer').then((module) => module.default)
    ),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/audit-log',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/components/Studies/AuditLog/AuditLog').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:studyApiId/exports/:exportApiId/download',
    private: true,
    exact: true,
    component: ExportDownloadConfirmation,
    skipApiIdMapping: true,
    preserveDeepLinkOnLogin: true,
  },
  {
    path: '/exports/:exportApiId/download',
    private: true,
    exact: true,
    component: ExportDownloadConfirmation,
    skipApiIdMapping: true,
    preserveDeepLinkOnLogin: true,
  },
  {
    path: '/studies/:id/benchling-sync',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Studies/BenchlingSync/BenchlingSync.tsx').then((module) => module.default)
    ),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/workflow',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Workflow/Show/WorkflowTemplateSetup').then((module) => module.WorkflowV2)
    ),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/cages',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/cages/cages').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/study-groups/:tab?',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Studies/StudyGroups/StudyGroups.tsx').then((module) => module.StudyGroups)
    ),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/graphs',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/groups/groups').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/animals/:subjectId',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/subjects/show').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/approvals/:approvalRequestId/:tab?',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/studies/approval').then((module) => withStudy(module.default))),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/requests',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/studies/requestCreate').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/requests/:requestId/:tab?',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/studies/requestEdit').then((module) => withStudy(module.default))),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/studies/:id/datatables/:dataTableId',
    private: true,
    exact: true,
    lazy: lazyRoute(() => import('@/pages/dataTables/Display').then((module) => module.default)),
    navigation: StudyNav,
    canCollapseNav: true,
  },
  {
    path: '/team',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/pages/team/manage').then((module) => resetReduxSettings(onlyTeamOwners(module.default)))
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/glossary/metadata',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/components/Glossary/Sections/Metadata/IndividualSaveMetadata').then((module) =>
        onlyTeamOwners(withSpacing(module.default))
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/glossary/observations',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/components/Glossary/Sections/Observations/ObservationGlossaries').then((module) =>
        onlyTeamOwners(withSpacing(withGlossary(module.default, 'observations')))
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/glossary/samples',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/components/Glossary/Sections/Samples/SampleGlossaries').then((module) =>
        onlyTeamOwners(withSpacing(withGlossary(module.default, 'samples')))
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/glossary/deceased',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/components/Glossary/Sections/TerminationReasons').then((module) =>
        onlyTeamOwners(withSpacing(withGlossary(module.default, 'euthanize_reasons')))
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/glossary/species-and-strains',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Glossary/Sections/SpeciesStrains').then((module) =>
        onlyTeamOwners(withSpacing(module.default))
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/glossary/treatments',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Glossary/Sections/Treatments/Treatments').then((module) =>
        onlyTeamOwners(withSpacing(module.default))
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/glossary/presets',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Glossary/Sections/Presets/Presets.tsx').then((module) =>
        onlyTeamOwners(withSpacing(module.Presets))
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/glossary/presets/:presetApiId/:presetAction',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Glossary/Sections/Presets/Builder/PresetBuilder.tsx').then((module) =>
        onlyTeamOwners(module.PresetBuilder)
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/glossary/presets/:presetAction',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Glossary/Sections/Presets/Builder/PresetBuilder.tsx').then((module) =>
        onlyTeamOwners(module.PresetBuilder)
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/studies/approvals/*',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/components/Glossary/Sections/Approvals').then((module) => onlyTeamOwners(withSpacing(module.default)))
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/studies/requests/*',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/components/Glossary/Sections/Requests').then((module) => onlyTeamOwners(module.default))
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/studies/cage-cards',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/components/Team/CageCards/Show').then((module) => onlyTeamOwners(withSpacing(module.default)))
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/studies/devices',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/components/RegisterDevices').then((module) => onlyTeamOwners(withSpacing(module.default)))
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/integrations/webhooks',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Webhooks/WebhookConfigurations.tsx').then((module) =>
        onlyTeamOwners(withSpacing(module.default))
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/integrations/webhooks/:webhookApiId/events',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Webhooks/WebhookEvents/WebhookEvents.tsx').then((module) =>
        onlyTeamOwners(withSpacing(module.default))
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/benchling-sync',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/BenchlingBackfill/BenchlingBackfill.tsx').then((module) =>
        onlyTeamOwners(withSpacing(module.default))
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/benchling-sync/:benchlingBackfillApiId/events',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/BenchlingBackfill/BackfillEvents.tsx').then((module) =>
        onlyTeamOwners(withSpacing(module.default))
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/studies/groups',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/components/Team/Groups').then((module) => onlyTeamOwners(withSpacing(module.default)))
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/studies/study-codes/*',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/pages/team/study-codes/StudyCode.routes').then((module) => onlyTeamOwners(withSpacing(module.default)))
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/studies/forms',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Team/Studies/Forms').then((module) => onlyTeamOwners(withSpacing(module.default)))
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/studies/forms/:formId',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Team/Studies/Forms/FormEdit').then((module) => onlyTeamOwners(module.default))
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/integrations/sso',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/components/Team/SingleSignOn').then((module) => onlyTeamOwners(withSpacing(module.default)))
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/integrations/api',
    private: true,
    exact: true,
    lazy: lazyRoute(() =>
      import('@/components/Team/ApiKeys').then((module) => onlyTeamOwners(withSpacing(module.default)))
    ),
    navigation: TeamNav,
  },
  {
    path: '/account',
    private: true,
    exact: true,
    component: resetReduxSettings(Account),
  },
  {
    path: '/create-account',
    lazy: lazyRoute(() => import('@/pages/create-account').then((module) => module.default)),
    private: true,
    exact: true,
  },
  {
    path: '/team/admin-console/team-config',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/components/Team/AdminConsole/TeamConfig/TeamConfig').then((module) =>
        onlySupportUsers(withSpacing(module.TeamConfig))
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/admin-console/feature-flags',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/components/Team/AdminConsole/FeatureFlagConfig').then((module) =>
        onlySupportUsers(withSpacing(module.FeatureFlagConfig))
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/admin-console/sync',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/components/Team/AdminConsole/IntegrationConfigV2.tsx').then((module) =>
        onlySupportUsers(withSpacing(module.IntegrationConfigV2))
      )
    ),
    navigation: TeamNav,
  },
  {
    path: '/team/admin-console/tickatlab',
    private: true,
    exact: false,
    lazy: lazyRoute(() =>
      import('@/components/Team/AdminConsole/TickatlabConfig.tsx').then((module) =>
        onlySupportUsers(withSpacing(module.TickatlabConfig))
      )
    ),
    navigation: TeamNav,
  },
];
